import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useApiData } from '@hozana/api/hooks/useApiData'
import { useEventListener } from '@hozana/hooks/useEventListener'
import { useInit } from '@hozana/hooks/useInit'
import { useIsHydrated } from '@hozana/hooks/useIsHydrated'
import { useDispatch } from '@hozana/redux/hooks'
import { useRouter } from '@hozana/router'
import { getFirstQueryValue, removeQueryValuesFromUrl } from '@hozana/router/functions'
import { getDisplayMode } from '@hozana/screen/functions/getDisplayMode'
import { cookie } from '@hozana/storage/cookies'
import { LOG_STATE } from '@hozana/tracking/constants'
import { GTM } from '@hozana/tracking/gtm'
import { hasLandingInfo, saveLandingInfo } from '@hozana/tracking/landingInfo'
import { getSessionInfo, saveSessionInfo } from '@hozana/tracking/sessionInfo'
import { saveSponsorInfos } from '@hozana/tracking/sponsor'

import { CONTENT_ID } from 'general/constants'
import { useCommonActions } from 'general/hooks/useCommonActions'
import { CookieManager } from 'general/managers/cookies/CookieManager'
import { COOKIES } from 'general/managers/cookies/constants'
import { FlashManager } from 'general/managers/flashs/FlashManager'
import { LangManager } from 'general/managers/languages/LangManager'
import { PopinsManager } from 'general/managers/popins/PopinsManager'
import { getStatisticsQuery } from 'general/queries'
import { Header } from 'general/structure/Header'
import { PAGE } from 'routes/constants'

import { loadToken } from 'modules/auth/functions'
import { useUser } from 'modules/user/hooks/useUser'

/**
 * ### AppContainer ###
 *
 * This is the main container, for nearly all the pages.
 * It loads all the managers ([see in the general docs](../general.readme.md)) for example.
 */
export const AppContainer: TFCC = ({ children }) => {
  const router = useRouter()
  const {
    i18n: { language },
  } = useTranslation()
  const dispatch = useDispatch()
  const me = useUser()
  const isHydrated = useIsHydrated()

  const { pathname, query } = router

  const currentInfo = useMemo(
    () =>
      __CLIENT__
        ? {
            referrer: window.document.referrer,
            landingUrl: window.location.href,
            landingDate: new Date(),
          }
        : undefined,
    [],
  )

  useInit(() => {
    if (!hasLandingInfo()) {
      saveLandingInfo(currentInfo)
    }
  }, __CLIENT__)

  useEffect(() => {
    const storedSessionInfo = getSessionInfo()

    if (!storedSessionInfo) {
      saveSessionInfo(currentInfo)
    }
  }, [currentInfo, dispatch])

  useEffect(() => {
    // GTM data is stored in a cookie to be sent to GTM when the GTM script is loaded
    //
    // This is to avoid:
    // - setting up GTM in full-ssr-functions
    // - losing data:
    //   - we want to send GTM events on click on links in guide pages
    //   - since these pages are not hydrated, the clicks on such links reload the page
    //   - so either the data would not be sent to GTM
    //   - or we would need to delay the navigation
    //
    // This logic must be present in the new frontend root layout as well to handle links to migrated pages
    //
    // #gtmData

    const gtmData = cookie.load(COOKIES.gtmData)

    if (gtmData) {
      GTM.push(gtmData)
      cookie.remove(COOKIES.gtmData)
    }
  }, [])

  useApiData(() => ({ query: getStatisticsQuery() }), [])

  useEffect(() => {
    GTM.push({
      displayMode: getDisplayMode(),
      isOnline: LOG_STATE.ONLINE,
      lang: language,
    })

    if (loadToken()) {
      GTM.trackLogin()
    } else {
      GTM.trackLogout()
    }
  }, [language])

  useEventListener(
    'beforeinstallprompt',
    (e: BeforeInstallPromptEvent) => {
      GTM.trackEvent(GTM.EVENTS.PWA_HOMESCREEN_PROMPTED)
      e.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === 'dismissed') {
          GTM.trackEvent(GTM.EVENTS.PWA_HOMESCREEN_DISMISSED)
        } else {
          GTM.trackEvent(GTM.EVENTS.PWA_HOMESCREEN_ADDED)
        }
      })
    },
    undefined,
    !__CLIENT__,
  )

  useCommonActions()

  // Save sponsor key (uik)
  useEffect(() => {
    if (!me.isLogged && query.uik) {
      saveSponsorInfos(getFirstQueryValue(query.uik))
    }
  }, [query.uik, me.isLogged])

  let bannerToDisplay = ['CookieBanner', 'LangBanner']

  // Handle withPopin and withBanner url query
  const withPopin = getFirstQueryValue(query.withPopin) || cookie.load(COOKIES.withPopin) // use for popins
  const withBanner = getFirstQueryValue(query.withBanner) || cookie.load(COOKIES.withBanner) // use for banners
  if (query.withPopin || query.withBanner) {
    if (query.withPopin) {
      cookie.save(COOKIES.withPopin, withPopin)
    }
    if (query.withBanner) {
      cookie.save(COOKIES.withBanner, withBanner)
    }
    removeQueryValuesFromUrl(router, ['withPopin', 'withBanner'])
  }
  if (withBanner && withBanner !== 'true') {
    bannerToDisplay = withBanner === 'false' ? [] : withBanner.split(',')
  }

  /** Don't show Header on following pages */
  const displayHeader = !([PAGE.AUTH_SOCIAL, PAGE.COMMUNITY_VIEW_LANDING, PAGE.HOME, PAGE.CONNECT] as PAGE[]).includes(
    pathname,
  )

  /** Don't show CookieBanner on following pages */
  const displayCookieBanner = !([PAGE.COMMUNITY_VIEW_LANDING, PAGE.AUTH_SOCIAL] as PAGE[]).includes(pathname)

  // Blocks are in their own div with an ID so that SSR hydration doesn't render them in the wrong div
  return (
    <>
      <div id="header">{displayHeader && <Header />}</div>
      <div id={CONTENT_ID}>{children}</div>
      <div id="blocks">
        <div id="popinManager">
          <PopinsManager />
        </div>
        <div id="flashManager">
          <FlashManager />
        </div>
        <div id="langManager">
          <LangManager />
        </div>
        {displayCookieBanner && isHydrated && bannerToDisplay.includes('CookieBanner') && (
          <div id="cookieManager">
            <CookieManager />
          </div>
        )}
      </div>
    </>
  )
}
